import { Box, Modal, Select, SelectOption, SelectProps, Typography } from '@chronosphereio/chrono-ui';
import { useCallback, useMemo, useState } from 'react';
import { PanelGroupDefinition, useListPanelGroups, usePanel, usePanelActions } from '@/dashboards/context';

export type MoveDirection = 'up' | 'down';

interface MovePanelModalProps {
  itemKey: string;
  currentGroupId: number;
  onClose: () => void;
  moveDirection: MoveDirection;
}

export const MovePanelModal = ({ itemKey, currentGroupId, onClose, moveDirection }: MovePanelModalProps) => {
  const panelGroups = useListPanelGroups().filter((group) => group.kind === 'Grid');

  const { movePanelToPanelGroup } = usePanelActions({
    panelGroupId: currentGroupId,
    panelGroupItemLayoutId: itemKey,
  });

  const {
    panelDefinition: {
      spec: {
        display: { name: panelName },
      },
    },
  } = usePanel({
    panelGroupId: currentGroupId,
    panelGroupItemLayoutId: itemKey,
  });

  return (
    <MovePanelModalContents
      itemKey={itemKey}
      currentGroupId={currentGroupId}
      onClose={onClose}
      panelGroups={panelGroups}
      movePanelToExistingGroup={movePanelToPanelGroup}
      panelName={panelName ?? ''}
      moveDirection={moveDirection}
    />
  );
};

interface MovePanelModalContentsProps extends MovePanelModalProps {
  panelGroups: PanelGroupDefinition[];
  movePanelToExistingGroup: (panelKey: string, groupId: number) => void;
  panelName: string;
}

export const MovePanelModalContents = ({
  itemKey,
  currentGroupId,
  onClose,
  panelGroups,
  panelName,
  movePanelToExistingGroup,
  moveDirection,
}: MovePanelModalContentsProps) => {
  const panelGroupOptions = useMemo(
    () =>
      panelGroups.map((group, index) => {
        const label = group.title ?? `Group ${index + 1}`;
        return {
          key: `${group.id}`,
          label: `${label}${group.id === currentGroupId ? ' (current)' : ''}`,
          groupId: group.id,
        };
      }),
    [panelGroups, currentGroupId]
  );

  const currentGroupIndex = panelGroups.findIndex((group) => group.id === currentGroupId);

  const initialSelectionIndex =
    moveDirection === 'up'
      ? Math.max(currentGroupIndex - 1, 0)
      : Math.min(currentGroupIndex + 1, panelGroups.length - 1);

  const [selectedGroup, setSelectedGroup] = useState(panelGroupOptions[initialSelectionIndex]);

  const handleGroupChange: SelectProps<SelectOption & { groupId: number }>['onChange'] = (e, value) => {
    if (value) {
      setSelectedGroup(value);
    }
  };

  const onSubmit = useCallback(() => {
    if (selectedGroup && selectedGroup.groupId !== currentGroupId) {
      movePanelToExistingGroup(itemKey, selectedGroup.groupId);
    }
    onClose();
  }, [movePanelToExistingGroup, itemKey, selectedGroup, onClose, currentGroupId]);

  return (
    <Modal
      open={true}
      title="Move panel"
      onPrimaryButtonClick={onSubmit}
      primaryButtonText="Move"
      onClose={onClose}
      size="small"
    >
      <Box pt={1}>
        <Typography>
          Select panel group to move panel &quot;{panelName !== '' ? panelName : 'Untitled Panel'}&quot; to:
        </Typography>
        <Box pt={2} width={200}>
          <Select
            disableClearable
            Label="Panel Group"
            options={panelGroupOptions}
            value={selectedGroup}
            onChange={handleGroupChange}
          ></Select>
        </Box>
      </Box>
    </Modal>
  );
};
